import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_AI_EDIT_HOTKEY, hotkeyFromEvent, hotkeyLabel, resolveHotkey } from "./aiEditHotkey";

/** "AI Edit hotkey" row for the editor settings popover. */
export default function AiEditHotkeySetting({ hotkey, onChange }) {
  const { t } = useTranslation("common");
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);
  const current = resolveHotkey(hotkey);
  const isDefault = hotkeyLabel(current) === hotkeyLabel(DEFAULT_AI_EDIT_HOTKEY);

  useEffect(() => {
    if (capturing) ref.current?.focus();
  }, [capturing]);

  const onKeyDown = (e) => {
    if (!capturing) return;
    if (e.key === "Escape") {
      setCapturing(false);
      setError("");
      return; // let the popover close too
    }
    e.preventDefault();
    e.stopPropagation();
    const res = hotkeyFromEvent(e.nativeEvent || e);
    if (!res) return;
    if (res.error) {
      setError(
        res.error === "needsModifier"
          ? t("editReportAi.hotkeyNeedsModifier", { defaultValue: "Use Ctrl, Alt or Cmd together with a key." })
          : t("editReportAi.hotkeyReserved", { defaultValue: "This key is already used. Choose another one." }),
      );
      return;
    }
    onChange?.(res.hotkey);
    setCapturing(false);
    setError("");
  };

  return (
    <div className="text-sm" data-testid="ai-edit-hotkey-setting">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-gray-800 dark:text-gray-100">
          {t("editReportAi.hotkeyLabel", { defaultValue: "AI Edit hotkey" })}
        </span>
        <button
          ref={ref}
          type="button"
          data-hotkey-capture
          onClick={() => {
            setCapturing((v) => !v);
            setError("");
          }}
          onKeyDown={onKeyDown}
          className={`min-w-[96px] rounded-md border px-2 py-1 text-center font-mono text-xs ${
            capturing
              ? "border-[#A4C639] bg-[#CBEA7B40] text-gray-700 dark:text-gray-100"
              : "border-gray-300 bg-gray-50 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          }`}
          title={t("editReportAi.hotkeyChange", { defaultValue: "Change the AI Edit hotkey" })}
        >
          {capturing ? t("editReportAi.hotkeyPress", { defaultValue: "Press keys…" }) : hotkeyLabel(current)}
        </button>
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {!isDefault && !capturing ? (
        <button
          type="button"
          className="mt-1 text-xs text-gray-500 hover:underline dark:text-gray-400"
          onClick={() => onChange?.(DEFAULT_AI_EDIT_HOTKEY)}
        >
          {t("editReportAi.hotkeyReset", { defaultValue: "Reset" })} ({hotkeyLabel(DEFAULT_AI_EDIT_HOTKEY)})
        </button>
      ) : null}
    </div>
  );
}
